/**
 * Checks if a value is not empty (not undefined, null, or empty string)
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is not empty
 */
const isNotEmpty = value => value !== undefined && value !== null && value !== ''

/**
 * Splits a value by semicolon and trims whitespace
 * @param {string} value - The value to split
 * @returns {Array<string>} Array of trimmed values
 */
const splitBySemicolon = value => {
  if (!isNotEmpty(value)) return []
  return String(value)
    .split(';')
    .map(v => v.trim())
    .filter(v => v !== '')
}

export const DEFAULT_PRODUCTS_CSV_RULE = {
  columns: [
    {name: 'Product ID', required: true, type: 'string', minChar: 0, maxChar: 255},
    {name: 'Product Name', required: true, type: 'string', minChar: 0, maxChar: 255},
    {name: 'Is_Returnable', required: false, type: 'string', minChar: 0, maxChar: 255},
    {name: 'Show on E-Commerce', required: false, type: 'boolean'},
    {name: 'Show on Engagement', required: false, type: 'boolean'},
    {name: 'Sorting', required: false, type: 'integer'},
  ],
  type: 'text/csv',
  size: '5120',
}

export const DEFAULT_SUGGESTED_SKU_CSV_RULE = {
  columns: [
    {name: 'Segment ID', required: true, type: 'string', minChar: 1, maxChar: 255},
    {name: 'ProductId', required: true, type: 'string', minChar: 1, maxChar: 255},
    {name: 'Priority', required: true, type: 'integer'},
  ],
  type: 'text/csv',
  size: '25000',
}

/**
 * Validates ID to name mapping consistency (supports multiple values separated by semicolon)
 * @param {Object} row - The CSV row data
 * @param {string} idColumn - The ID column name (e.g., 'Category Id')
 * @param {string} nameColumn - The name column name (e.g., 'Category')
 * @param {number} rowIndex - The row index (0-based)
 * @param {Map} idToNameMap - Map tracking ID to name mappings
 * @param {Array} errors - The errors array to populate
 */
const validateIdToNameMapping = (row, idColumn, nameColumn, rowIndex, idToNameMap, errors) => {
  const idValue = row[idColumn]
  const nameValue = row[nameColumn]

  const ids = splitBySemicolon(idValue)
  const names = splitBySemicolon(nameValue)

  if (ids.length === 0 || names.length === 0) {
    return
  }

  if (ids.length !== names.length) {
    errors.push(
      `Row ${rowIndex + 1}: '${idColumn}' has ${ids.length} value(s) but '${nameColumn}' has ${names.length} value(s). They must have the same count.`
    )
    return
  }

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]
    const name = names[i]

    if (isNotEmpty(id) && isNotEmpty(name)) {
      if (idToNameMap.has(id)) {
        const existingName = idToNameMap.get(id)
        if (existingName !== name) {
          errors.push(
            `Row ${rowIndex + 1}: '${idColumn}' "${id}" is mapped to different '${nameColumn}' values "${existingName}" and "${name}".`
          )
        }
      } else {
        idToNameMap.set(id, name)
      }
    }
  }
}

/**
 * Validates ID to multiple name mappings (for localized fields)
 * @param {Object} row - The CSV row data
 * @param {string} idColumn - The ID column name (e.g., 'Brand Id')
 * @param {Array<{column: string, map: Map}>} nameMappings - Array of name column mappings to validate
 * @param {number} rowIndex - The row index (0-based)
 * @param {Array} errors - The errors array to populate
 */
const validateIdToMultipleNames = (row, idColumn, nameMappings, rowIndex, errors) => {
  nameMappings.forEach(({column, map}) => {
    validateIdToNameMapping(row, idColumn, column, rowIndex, map, errors)
  })
}

/**
 * Creates a generic CSV validator function based on field mapping configuration
 * @param {Array<{idColumn: string, nameColumns: string[]}>} fieldMappings - Configuration for ID to name mappings
 * @returns {Function} A validator function that takes (row, rowIndex, errors)
 *
 * @example
 * const validator = createCsvValidator([
 *   { idColumn: 'Category Id', nameColumns: ['Category'] },
 *   { idColumn: 'Brand Id', nameColumns: ['Brand_es_MX', 'Brand_en'] }
 * ])
 */
export const createCsvValidator = fieldMappings => {
  const maps = fieldMappings.map(({idColumn, nameColumns}) => ({
    idColumn,
    nameMappings: nameColumns.map(column => ({
      column,
      map: new Map(),
    })),
  }))

  return (row, rowIndex, errors) => {
    maps.forEach(({idColumn, nameMappings}) => {
      validateIdToMultipleNames(row, idColumn, nameMappings, rowIndex, errors)
    })
  }
}

/**
 * Creates a validator function for Category, Subcategory, and Brand Id to name consistency
 * @returns {Function} A validator function that takes (row, rowIndex, errors)
 */
export const createProductValidator = () => {
  return createCsvValidator([
    {idColumn: 'Category Id', nameColumns: ['Category']},
    {idColumn: 'Subcategory Id', nameColumns: ['Subcategory_es_MX', 'Subcategory_en']},
    {idColumn: 'Brand Id', nameColumns: ['Brand_es_MX', 'Brand_en']},
  ])
}

/**
 * Apply country-specific overrides to a base CSV rules object.
 * - Merges top-level properties (`type`, `size`) when provided by overrides.
 * - Merges `columns` by `name`: existing columns are shallow-merged, new columns are appended.
 *
 * @param {Object} baseRules - Base CSV rules (e.g. from feature flag)
 * @param {string} countryCode - Country code to look up overrides for
 * @param {Object} [overridesByCountry={}] - Map of countryCode -> overrideRules
 * @returns {Object} New rules object with country-specific overrides applied
 */
const INVENTORY_CSV_COUNTRY_OVERRIDES = {
  
}

/**
 * Get inventory CSV overrides for a specific country.
 * @param {string} countryCode
 * @returns {Object|undefined}
 */
export const getInventoryCountryOverride = countryCode => INVENTORY_CSV_COUNTRY_OVERRIDES[countryCode]

/**
 * Get inventory CSV rules for a specific country, with overrides applied.
 * @param {Object} baseRules
 * @param {string} countryCode
 * @returns {Object}
 */
export const getInventoryCsvRules = (baseRules = {}, countryCode) =>
  applyCountryOverridesToCsvRules(baseRules, countryCode, INVENTORY_CSV_COUNTRY_OVERRIDES)

export const applyCountryOverridesToCsvRules = (baseRules = {}, countryCode, overridesByCountry = {}) => {
  if (!countryCode) return baseRules

  const countryOverrides = overridesByCountry[countryCode]
  if (!countryOverrides) return baseRules

  const merged = {...baseRules}

  // Merge top-level simple props
  if (countryOverrides.type) merged.type = countryOverrides.type
  if (countryOverrides.size) merged.size = countryOverrides.size

  const baseColumns = Array.isArray(baseRules.columns) ? baseRules.columns.slice() : []
  const overrideColumns = Array.isArray(countryOverrides.columns) ? countryOverrides.columns : []

  const columnMap = new Map()
  baseColumns.forEach(col => {
    if (col && col.name) columnMap.set(col.name, {...col})
  })

  overrideColumns.forEach(ocol => {
    if (!ocol || !ocol.name) return
    if (columnMap.has(ocol.name)) {
      const mergedCol = {...columnMap.get(ocol.name), ...ocol}
      columnMap.set(ocol.name, mergedCol)
    } else {
      columnMap.set(ocol.name, {...ocol})
    }
  })

  merged.columns = Array.from(columnMap.values())

  return merged
}
